import { useCallback, useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, Modal, tokens, useNotify } from '@hotjar/hotsauce';
import { useErrorHandler } from '@hotjar/error-handler';
import { useProductCatalog } from '@hotjar/product-catalog';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { useCurrentSiteAccount, useOwnedAccount } from '@hotjar/account';
import type { ProductAllowance, SelectedPlans } from '@hotjar/subscription-management';
import {
  mapSelectedPlansToV3ProductAllowances,
  useIsCancellingSubscription,
} from '@hotjar/subscription-management';
import type { Site } from '@hotjar/site';
import { useGetFreeTierProducts } from '@hotjar/webapp/domains/common/product/hooks/useGetFreeTierProducts';
import { LOGOUT } from '@hotjar/webapp/constants/routes/auth';
import {
  DatadogActionType,
  DatadogUpsellType,
} from '@hotjar/webapp/domains/revenue/upsell/constants';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { upsellLogger } from '@hotjar/webapp/domains/revenue/utils';
import { useBillingPeriodState, useAnnualBillingEligibility, getSalesLink } from '@hotjar/upsell';
import {
  getProductBySku,
  getProductSkusByPlanId,
} from '@hotjar/webapp/domains/common/product/utils';

import { useQualifyingSalesLead } from '../../hooks/useQualifyingSalesLead';
import type {
  AnalyticsProduct,
  FeedbackProduct,
} from '../../external-dependencies/domains/product/types';
import { ProductType } from '../../external-dependencies/domains/product/types';
import { useTrack } from '../../external-dependencies/domains/tracking/useTrack';
import { useModalLoadedTracking } from '../../external-dependencies/hooks/useModalLoadedTracking';
import { MODAL } from '../../constants';
import { TrialsLegacyContext } from '../../contexts/TrialsLegacyContext';
import { useUpsellCheckout } from '../../external-dependencies/domains/revenue/hooks/useUpsellCheckout';
import { ChurnDeflectionModal } from './components/ChurnDeflectionModal/ChurnDeflectionModal';
import { DowngradeMessage } from './components/DowngradeMessage/DowngradeMessage';
import { ExpiredIllustration } from './components/ExpiredIllustration/ExpiredIllustration';
import { SubTitle } from './components/ModalSubtitle/ModalSubtitle';
import { Title } from './components/ModalTitle/ModalTitle';
import { SiteList } from './components/SiteList/SiteList';
import { ModalStyles as S } from './PostTrialModal.styles';

export const PostTrialModal = (): JSX.Element => {
  const [downgradeModalIsOpen, setDowngradeModalIsOpen] = useState(false);
  const { t } = useTranslation('trials', { keyPrefix: 'post-trial' });
  const { click: trackClick, getGroupDataForSite } = useTrack();
  const {
    currentSite,
    isLoading: contextIsLoading,
    sites = [],
    productCatalog: productCatalogV2,
  } = useContext(TrialsLegacyContext);
  const isLargeScreen = useMedia(tokens.breakpoint.minWidth.xl);
  const [selectedPlans, setSelectedPlans] = useState<SelectedPlans>({}); // legacy plans
  const canSelfServe = Object.values(selectedPlans).every(
    (siteProducts) =>
      siteProducts[ProductType.Analytics]?.selfServeEnabled &&
      siteProducts[ProductType.Feedback]?.selfServeEnabled,
  );
  const { data: account } = useOwnedAccount();
  const { data: currentSiteAccount } = useCurrentSiteAccount();
  const {
    value: selectedBillingPeriod,
    setValue: setSelectedBillingPeriod,
    isLoading: billingPeriodStateIsLoading,
    isChanged: billingPeriodChanged,
  } = useBillingPeriodState(currentSiteAccount);

  /**
   * We must ignore the selected billing period if user is downgrading since the user might not have payment details
   */
  const newBillingPeriod = downgradeModalIsOpen ? undefined : selectedBillingPeriod;
  const { submitCheckout, isLoading: useUpsellCheckoutIsLoading } = useUpsellCheckout({
    account: currentSiteAccount,
    newBillingPeriod,
  });
  const { showToast } = useNotify();
  const isQualifyingSalesLead = useQualifyingSalesLead({ sites });
  const isBillingV3 = isBillingV3Enabled(account);
  const { data: productCatalogV3 } = useProductCatalog({
    enabled: isBillingV3,
    billingPeriod: 'MONTHLY',
    currency: 'EUR',
  });
  const navigate = useNavigate();
  const handleError = useErrorHandler();

  const [selectedProductAllowances, setSelectedProductAllowances] = useState<ProductAllowance[]>(
    [],
  );
  const isCancellingSubscription: boolean = useIsCancellingSubscription({
    productAllowances: selectedProductAllowances, // selectedProductAllowances contains all the allowances (existing and edited ones)
    sites,
    isBillingV3,
    billingPeriod: 'MONTHLY',
    currency: 'EUR',
  });
  const { feedback_product: freeAsk, analytics_product: freeObserve } = useGetFreeTierProducts();
  const { isEligible, isLoading: isEligibleIsLoading } =
    useAnnualBillingEligibility(currentSiteAccount);

  const closeDowngradeModal = () => {
    setDowngradeModalIsOpen(false);
  };

  const openChurnDeflectionModal = () => {
    if (currentSite) {
      trackClick(MODAL.name, MODAL.downgradeToBasic, getGroupDataForSite(currentSite));
    }
    setDowngradeModalIsOpen(true);
  };

  const downgrade = async () => {
    if (!account || !currentSite || sites?.length == null) {
      throw new Error(
        'Failed to submit downgrade in post-trial modal: missing account, current site, or sites.',
      );
    }

    trackClick(MODAL.name, MODAL.downgradeToBasicConfirm, getGroupDataForSite(currentSite));

    const freeSelectedPlans = Object.fromEntries(
      Object.keys(selectedPlans).map((key) => [
        key,
        {
          [ProductType.Analytics]: freeObserve,
          [ProductType.Feedback]: freeAsk,
        },
      ]),
    );

    return submitCheckout({
      referrer: 'post-tria-modal__upsell', // FIXME: there's a typo in this and the formatting is weird too, should we fix it?
      selectedPlans: freeSelectedPlans as SelectedPlans, // FIXME: quick ts workaround
      forceLegacyCheckout: true,
    });
  };

  const onDowngrade = async () => {
    try {
      const { upsellFlowFinished } = await downgrade();

      if (upsellFlowFinished) {
        showToast({
          kind: 'success',
          text: t('downgrade-notification-label'),
        });
      }
    } catch (error) {
      if (Boolean(error) && error instanceof Error) {
        handleError(error);
      }
    } finally {
      closeDowngradeModal();
    }
  };

  const onPlanChange = useCallback(
    (site: Site, selectedPlan: AnalyticsProduct | FeedbackProduct) => {
      setSelectedPlans((prevSelectedState) => {
        return {
          ...prevSelectedState,
          [site.id]: {
            ...(prevSelectedState[site.id] ?? {}),
            [selectedPlan.type]: selectedPlan,
          },
        };
      });
    },
    [setSelectedPlans],
  );

  const onLogout = async () => {
    trackClick('Logout', MODAL.name, {});
    navigate(LOGOUT.path);
  };

  useEffect(() => {
    if (isBillingV3) {
      setSelectedProductAllowances(
        mapSelectedPlansToV3ProductAllowances({
          selectedPlans,
          productCatalog: productCatalogV3,
          sites,
        }),
      );
    }
  }, [isBillingV3, productCatalogV3, selectedPlans, sites]);

  const onCheckoutClick = async (): Promise<boolean> => {
    const isDowngradingAllPlansToBasic = Object.values(selectedPlans).every(
      (product) =>
        product[ProductType.Analytics]?.tier === 'basic' &&
        product[ProductType.Analytics]?.tier === 'basic',
    );

    if (currentSite) {
      trackClick(MODAL.name, MODAL.checkoutButton, getGroupDataForSite(currentSite));
    }

    if (isDowngradingAllPlansToBasic) {
      openChurnDeflectionModal();
      return true;
    }

    try {
      // todo: this is an async operation and should be handled by the handleError
      await submitCheckout({
        referrer: 'post-tria-modal__upsell', // FIXME: there's a typo in this and the formatting is weird too, should we fix it?
        selectedPlans,
      });

      return true;
    } catch (error) {
      if (Boolean(error) && error instanceof Error) {
        handleError(error);
      }

      return false;
    }
  };

  useModalLoadedTracking({
    modalName: MODAL.name,
    modalVersion: MODAL.version,
    referrer: '',
    site: currentSite,
  });

  useEffectOnceWithDependencies(
    () => {
      upsellLogger?.info('Post Trial modal opened', {
        upsellType: DatadogUpsellType.PostTrial,
        action: DatadogActionType.Open,
      });
    },
    [],
    () => !!currentSite,
  );

  const siteListIsLoading = contextIsLoading || isEligibleIsLoading || billingPeriodStateIsLoading;

  useEffectOnceWithDependencies(
    () => {
      const siteCurrentProducts =
        sites?.map((site) => {
          const skus = getProductSkusByPlanId(site.planId);
          const [observeProduct, askProduct] = skus.map((sku) =>
            getProductBySku(sku, productCatalogV2),
          );

          return [
            site.id,
            {
              [ProductType.Analytics]: observeProduct,
              [ProductType.Feedback]: askProduct,
            },
          ];
        }) ?? [];

      setSelectedPlans(Object.fromEntries(siteCurrentProducts));
    },
    [productCatalogV2, siteListIsLoading, sites],
    () => !siteListIsLoading,
  );

  return (
    <Modal
      size="fullscreen"
      data-testid="post-trial-modal"
      contentLabel={t('title', { context: 'label' })}
      hideCloseButton
      isOpen
    >
      <S.Body>
        <S.LogoutRow>
          <Button kind="secondary" icon="off" size="m" onClick={onLogout}>
            {t('logout')}
          </Button>
        </S.LogoutRow>
        <S.Container>
          <S.PlansColumn>
            <Title />
            <SubTitle isQualifyingSalesLead={isQualifyingSalesLead} />

            {isLargeScreen && (
              <S.Illustration>
                <ExpiredIllustration />
              </S.Illustration>
            )}

            <SiteList
              selectedPlans={selectedPlans}
              onPlanChange={onPlanChange}
              billingPeriod={selectedBillingPeriod}
              onBillingPeriodChange={setSelectedBillingPeriod}
              billingPeriodChanged={billingPeriodChanged}
              isLoading={siteListIsLoading}
              sites={sites}
              productCatalogV2={productCatalogV2}
              productCatalogV3={productCatalogV3}
              ownedAccount={account}
              isAnnualBillingEligible={isEligible}
              canSelfServe={canSelfServe}
            />
            <DowngradeMessage sites={sites} />
            <ChurnDeflectionModal
              isOpen={downgradeModalIsOpen}
              onCloseModal={closeDowngradeModal}
              onDowngrade={onDowngrade}
              account={account}
              isLoading={useUpsellCheckoutIsLoading}
            />
          </S.PlansColumn>
        </S.Container>
      </S.Body>
      <Modal.Footer style={{ borderTop: `1px solid ${tokens.color.ui_border_light}` }}>
        <Button
          kind={isCancellingSubscription ? 'danger' : 'ghost'}
          onClick={openChurnDeflectionModal}
        >
          {t('downgrade')}
        </Button>
        {!isCancellingSubscription && canSelfServe && (
          <Button
            kind="primary"
            onClick={onCheckoutClick}
            isDisabled={isBillingV3 && !productCatalogV3}
          >
            {t('continue')}
          </Button>
        )}
        {!isCancellingSubscription && !canSelfServe && (
          <Button as="a" href={getSalesLink('BillingPage')} isExternal>
            {t('sales-cta')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
